/**
 * SplitBillingChainBadge — Phase 3F.5 (dev mode)
 * ────────────────────────────────────────────────────────────────────
 * A compact "chain status" pill rendered inside the shift drawer next to
 * the Service Activity Breakdown panel. It calls the Phase 3F trace
 * endpoint and surfaces a single-line summary so coordinators can see at
 * a glance whether the 4-layer split-billing chain is intact for the
 * draft batch associated with this shift.
 *
 * Visual states:
 *   • Gray   — no chain found (allocations exist but no draft/invoice yet)
 *   • Green  — all layers balanced
 *   • Amber  — WARN-level drift
 *   • Red    — CRITICAL drift
 *
 * Read-only. No mutations. Hidden when the master flag is OFF (the trace
 * endpoint reports flag_state in the payload).
 */
import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { CheckCircle2, AlertTriangle, XCircle, Link2 } from 'lucide-react';

function pill(style, Icon, label, testid) {
  return (
    <div
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs ${style}`}
      data-testid={testid}
    >
      <Icon className="h-3.5 w-3.5" />
      <span className="font-medium">{label}</span>
    </div>
  );
}

export function SplitBillingChainBadge({ shiftId }) {
  const [state, setState] = useState({ loading: true, found: null, trace: null });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!shiftId) return;
      setState((s) => ({ ...s, loading: true }));
      try {
        // Step 1: look up any draft_invoice_fanout batch tied to this shift.
        // We hit the trace endpoint by shift→batch resolution: the trace
        // endpoint accepts a batch_id. Since the drawer only knows the shift
        // id, we first try the invoice for this shift, then trace by its
        // batch_id. Falls back gracefully.
        const inv = await api
          .get('/invoices', { params: { source_shift_id: shiftId } })
          .catch(() => null);
        const matches = inv?.data?.data || [];
        const splitInvoice = (matches || []).find((x) => x.is_split_billed);
        const batchId = splitInvoice?.allocation_batch_id;
        if (!batchId) {
          if (!cancelled) setState({ loading: false, found: false, trace: null });
          return;
        }
        const trace = await api.get(`/admin/dev/split-billing-trace/${batchId}`);
        if (!cancelled) setState({ loading: false, found: true, trace: trace.data?.data });
      } catch {
        if (!cancelled) setState({ loading: false, found: false, trace: null });
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [shiftId]);

  if (state.loading) {
    return pill(
      'border-slate-200 bg-slate-50 text-slate-500',
      Link2,
      'Checking chain…',
      'split-chain-loading'
    );
  }
  if (!state.found || !state.trace) {
    return pill(
      'border-slate-200 bg-slate-50 text-slate-500',
      Link2,
      'Operational only — no split billing yet',
      'split-chain-none'
    );
  }
  const t = state.trace;
  const s = t.summary || {};
  const balanced = s.is_balanced;
  const lines = s.invoice_line_count ?? 0;
  const total = (s.sum_invoice_amount ?? 0).toFixed(2);
  if (balanced === true) {
    return pill(
      'border-emerald-200 bg-emerald-50 text-emerald-800',
      CheckCircle2,
      `Split-billed · ${lines} lines · $${total} · balanced`,
      'split-chain-balanced'
    );
  }
  if (balanced === false) {
    // Detect the worst severity from the drift collection via a quick GET
    return pill(
      'border-red-200 bg-red-50 text-red-800',
      XCircle,
      `Split-billed · ${lines} lines · $${total} · DRIFT detected`,
      'split-chain-drift'
    );
  }
  // null is_balanced (no claim/deduction context) — info only
  return pill(
    'border-amber-200 bg-amber-50 text-amber-800',
    AlertTriangle,
    `Split-billed · ${lines} lines · $${total} · pending`,
    'split-chain-pending'
  );
}

export default SplitBillingChainBadge;
